import { useEffect, useState } from "react";
import BASE_URL from "../config";

// The storefront menu, footer and home sections are partly hard-coded, so the
// API tells us what the admin has hidden (GET /categories/hidden-list) and the
// UI drops it. Kept short so an admin's change shows up within a minute.
const TTL_MS = 60 * 1000;

let cached = null;
let cachedAt = 0;
let inFlight = null;

// "Rudrakshas" / "rudraksha" / "Shankh & Sea Shells" -> comparable keys. The
// trailing "s" is dropped so a plural menu label still matches its API slug.
export const normalizeKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/s$/, "");

const EMPTY = { categories: new Set(), subcategories: new Set() };

const build = (data) => ({
  categories: new Set(
    (data?.categories || []).flatMap((c) => [normalizeKey(c.slug), normalizeKey(c.name)])
  ),
  subcategories: new Set(
    (data?.subcategories || []).flatMap((s) => [normalizeKey(s.slug), normalizeKey(s.name)])
  ),
});

export const getHiddenCategories = async () => {
  if (cached && Date.now() - cachedAt < TTL_MS) return cached;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const response = await fetch(`${BASE_URL}/categories/hidden-list`);
      if (!response.ok) throw new Error(`hidden-list failed: ${response.status}`);
      const result = await response.json();
      cached = result?.success ? build(result.data) : EMPTY;
    } catch {
      // Backend unreachable: show everything rather than blank the site.
      cached = EMPTY;
    } finally {
      cachedAt = Date.now();
      inFlight = null;
    }
    return cached;
  })();

  return inFlight;
};

const matches = (set, keys) => keys.some((k) => k && set.has(normalizeKey(k)));

export const useHiddenCategories = () => {
  const [hidden, setHidden] = useState(cached || EMPTY);

  useEffect(() => {
    let cancelled = false;
    getHiddenCategories().then((value) => {
      if (!cancelled) setHidden(value);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    isCategoryHidden: (...keys) => matches(hidden.categories, keys),
    isSubcategoryHidden: (...keys) => matches(hidden.subcategories, keys),
  };
};

// Drops hidden categories - and hidden subcategories inside the visible ones -
// from a menu tree shaped like Header/MenuData.js.
export const filterMenu = (menuData, { isCategoryHidden, isSubcategoryHidden }) =>
  menuData.map((menu) => {
    if (!menu.categories) return menu;
    return {
      ...menu,
      categories: menu.categories
        .filter((cat) => !isCategoryHidden(cat.slug, cat.title))
        .map((cat) => ({
          ...cat,
          items: (cat.items || []).filter((item) => !isSubcategoryHidden(item.slug)),
        })),
    };
  });
